import { useId, useRef, useState } from 'react'
import type { KeyboardEvent } from 'react'
import { cn } from '@/utils/cn'
import { focusRing, segmentedRest, segmentedSelected } from '@/components/ui/buttonStyles'
import { SrsSlider } from '../SrsSlider'
import { SRS_SLIDER_GROUPS, type SrsSliderConfig } from '../srsSliders'
import type { SrsSettings } from '@/types/admin'

interface ParameterDeckProps {
  draft: SrsSettings
  saved: SrsSettings
  onChange: (key: keyof SrsSettings, value: number) => void
  /** Whether the current plan contains a slip — the After a slip group only acts on one. */
  hasSlip: boolean
  onTrySlip: () => void
}

const SLIP_GROUP = 'After a slip'

/**
 * The scheduling parameters as a tabbed deck that sits under the timeline, one
 * group at a time, so every slider is within reach of the curve it bends. Each
 * slider says what it does right beneath it.
 */
export function ParameterDeck({ draft, saved, onChange, hasSlip, onTrySlip }: ParameterDeckProps) {
  const id = useId()
  const [tab, setTab] = useState(0)
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([])
  const group = SRS_SLIDER_GROUPS[tab]
  const changed = (slider: SrsSliderConfig) => draft[slider.key] !== saved[slider.key]
  const groupChanged = group.sliders.some(changed)

  function handleKeyDown(event: KeyboardEvent) {
    const count = SRS_SLIDER_GROUPS.length
    const next =
      event.key === 'ArrowRight' ? (tab + 1) % count : event.key === 'ArrowLeft' ? (tab + count - 1) % count : null
    if (next === null) return
    event.preventDefault()
    setTab(next)
    tabRefs.current[next]?.focus()
  }

  return (
    <section aria-labelledby={`${id}-title`} className="rounded-md border border-border-divider bg-surface-bg/40 p-5">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 id={`${id}-title`} className="font-serif text-lg text-heading">
          Parameters
        </h3>
        {groupChanged && (
          <button
            type="button"
            onClick={() => group.sliders.forEach((slider) => onChange(slider.key, saved[slider.key]))}
            className={cn('rounded px-1 font-mono text-xs uppercase tracking-widest text-muted hover:text-body', focusRing)}
          >
            Reset group ↺
          </button>
        )}
      </div>

      <div role="tablist" aria-label="Parameter groups" className="mt-4 flex flex-wrap gap-2">
        {SRS_SLIDER_GROUPS.map((g, i) => (
          <button
            key={g.title}
            ref={(el) => {
              tabRefs.current[i] = el
            }}
            id={`${id}-tab-${i}`}
            type="button"
            role="tab"
            aria-selected={i === tab}
            aria-controls={`${id}-panel`}
            tabIndex={i === tab ? 0 : -1}
            onClick={() => setTab(i)}
            onKeyDown={handleKeyDown}
            className={cn(
              'relative min-h-11 rounded-full px-4 font-serif text-sm transition-colors duration-200',
              i === tab ? segmentedSelected : segmentedRest,
              focusRing,
            )}
          >
            {g.title}
            {g.sliders.some(changed) && (
              <span className="absolute right-3 top-2 h-1.5 w-1.5 rounded-full bg-em">
                <span className="sr-only">, unsaved changes</span>
              </span>
            )}
          </button>
        ))}
      </div>

      <div id={`${id}-panel`} role="tabpanel" aria-labelledby={`${id}-tab-${tab}`} className="mt-5 space-y-5">
        {group.note && <p className="rounded-md bg-surface-bg px-4 py-3 font-serif text-sm text-body">{group.note}</p>}
        {group.title === SLIP_GROUP && !hasSlip && (
          <p className="flex flex-wrap items-baseline gap-x-2 rounded-md bg-surface-bg px-4 py-3 font-serif text-sm text-body">
            These act only after a slip, and the current plan has none.
            <button
              type="button"
              onClick={onTrySlip}
              className={cn('rounded font-serif text-sm text-accent underline underline-offset-4 hover:text-em', focusRing)}
            >
              Try it on a known word
            </button>
          </p>
        )}
        <div className="grid gap-x-8 gap-y-5 sm:grid-cols-2">
          {group.sliders.map((slider) => (
            <SrsSlider
              key={slider.key}
              id={`srs-${slider.key}`}
              label={slider.label}
              hint={slider.hint}
              value={draft[slider.key]}
              savedValue={saved[slider.key]}
              min={slider.min}
              max={slider.max}
              step={slider.step}
              suffix={slider.suffix}
              format={slider.format}
              onChange={(value) => onChange(slider.key, value)}
            />
          ))}
        </div>
      </div>
    </section>
  )
}
